import { useEffect, useRef, useState } from "react";

/**
 * Double-click to edit. Auto-focuses on activation, saves on Enter or blur,
 * cancels on Escape. Keeps the cell layout stable while editing.
 *
 * Props:
 *   value: current display value
 *   onSave: async (newValue) => void
 *   type: "text" | "number"
 *   render: (value) => ReactNode  optional custom read-mode renderer
 */
export default function InlineEditCell({ value, onSave, type = "text", render, className = "", testid, placeholder = "" }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setVal(value ?? ""); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = async () => {
    if (String(val) === String(value ?? "")) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(val);
      setEditing(false);
    } catch {
      setVal(value ?? "");
      setEditing(false);
    } finally { setSaving(false); }
  };
  const cancel = () => { setVal(value ?? ""); setEditing(false); };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type={type}
        value={val}
        disabled={saving}
        placeholder={placeholder}
        onChange={(e)=>setVal(type === "number" ? e.target.value.replace(/\D/g,"") : e.target.value)}
        onBlur={commit}
        onKeyDown={(e)=>{
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        data-testid={testid ? `${testid}-input` : undefined}
        className={`w-full bg-slate-800 border border-amber-500/50 text-white rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-amber-400 ${className}`}
      />
    );
  }
  return (
    <button
      type="button"
      onDoubleClick={()=>setEditing(true)}
      title="Double-click to edit"
      data-testid={testid}
      className={`text-left w-full cursor-cell rounded px-1 py-0.5 hover:bg-amber-500/10 hover:ring-1 hover:ring-amber-500/30 transition-colors ${className}`}>
      {render ? render(value) : (value ?? <span className="text-slate-600">—</span>)}
    </button>
  );
}
